import liff from '@line/liff';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL ?? 'https://your-backend.example.com';
const LIFF_ID = import.meta.env.VITE_LIFF_ID ?? '';
const LINE_ACCOUNT_ID = import.meta.env.VITE_LINE_ACCOUNT_ID ?? '@your-line-account';

/**
 * URLからクリックID・UTMパラメータを取得する
 */
function extractParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  const result: Record<string, string> = {};

  const keys = [
    'gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'yclid', 'lineclid', 'oppref',
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  ];

  for (const key of keys) {
    const value = params.get(key);
    if (value) result[key] = value;
  }

  // LINE広告タグのデフォルトパラメータ名 ldtag_cl も lineclid として受け取る
  if (!result['lineclid']) {
    const ldtagCl = params.get('ldtag_cl');
    if (ldtagCl) result['lineclid'] = ldtagCl;
  }

  return result;
}

/** LIFF が自分で付けるクエリ。ASP のクリックIDではないので送らない */
const LIFF_INTERNAL_KEYS = new Set(['liff.state', 'liff.referrer', 'liffClientId', 'liffRedirectUri', 'code', 'state', 'ldtag_cl', 'lct_to']);

/**
 * 既知キー以外のクエリ（ASP / ADNW 独自のクリックID: a8, atnct など）を拾う。
 * 最終的な絞り込み（キー名・件数・長さ・禁止キー）はサーバ側で行う。
 */
function extractExtraParams(known: Record<string, string>): Record<string, string> | undefined {
  const params = new URLSearchParams(window.location.search);
  const extra: Record<string, string> = {};
  params.forEach((value, key) => {
    if (!value || key in known || LIFF_INTERNAL_KEYS.has(key) || key.startsWith('utm_')) return;
    extra[key] = value;
  });
  return Object.keys(extra).length > 0 ? extra : undefined;
}

/**
 * バックエンドのトラッキングAPIにデータを送信する
 */
async function sendTrackingData(
  lineUserId: string,
  idToken: string | null,
  params: Record<string, string>
): Promise<void> {
  const payload = {
    liff_id: LIFF_ID,
    line_user_id: lineUserId,
    // バックエンドが検証すべきチャネル（line_login_channel_id 設定済み）では、これを検証して
    // line_user_id の代わりに真正な sub を採用する。未対応チャネル・null（openidスコープ未設定）
    // は無視される後方互換設計 (#263)。
    ...(idToken ? { id_token: idToken } : {}),
    lp_url: document.referrer || window.location.href,
    ...params,
    ...(extractExtraParams(params) ? { extra_params: extractExtraParams(params) } : {}),
  };

  const response = await fetch(`${BACKEND_URL}/api/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Track API error: ${response.status}`);
  }
}

/**
 * 友だち追加URLにリダイレクトする
 */
function redirectToFriendAdd(): void {
  // LINEアプリ内での友だち追加URLスキーム
  window.location.href = `https://line.me/R/ti/p/${LINE_ACCOUNT_ID}`;
}

/**
 * ステータスメッセージを表示する
 */
function showStatus(message: string, isError = false): void {
  const el = document.getElementById('status');
  if (el) {
    el.textContent = message;
    el.style.color = isError ? '#e74c3c' : '#2c3e50';
  }
}

/**
 * LINE経由のWeb CV (#377): 配信のリンク https://liff.line.me/<LIFF_ID>?lct_to=<サイトURL> で開かれたとき、
 * バックエンドに署名トークンつきの遷移先を作らせてサイトへ移る（LINE ユーザーIDは URL に出さない）。
 *
 * 遷移先はチャネルに登録したホストだけをバックエンドが許可する。バックエンドが応答しないときに
 * lct_to へそのまま飛ばすとオープンリダイレクトになるので、自動では飛ばず、行き先を見せて押してもらう。
 */
async function goToWebDestination(lineUserId: string, idToken: string | null, to: string): Promise<void> {
  showStatus('移動しています...');
  try {
    const response = await fetch(`${BACKEND_URL}/api/web-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ liff_id: LIFF_ID, line_user_id: lineUserId, ...(idToken ? { id_token: idToken } : {}), to }),
    });
    if (response.ok) {
      const data = (await response.json()) as { url: string };
      window.location.replace(data.url);
      return;
    }
    if (response.status === 400) {
      showStatus('このリンクの移動先は登録されていません。', true);
      return;
    }
    throw new Error(`web-link ${response.status}`);
  } catch (error) {
    console.error('[LIFF] web-link 失敗:', error);
    let host = '';
    try { host = new URL(to).host; } catch { /* 不正な URL */ }
    if (!host) {
      showStatus('リンクが正しくありません。', true);
      return;
    }
    showStatus(`移動先: ${host}`);
    const fallback = document.getElementById('fallback');
    if (fallback) {
      const a = document.createElement('a');
      a.href = to;
      a.rel = 'noreferrer';
      a.textContent = `${host} へ移動する`;
      fallback.replaceChildren(a);
      fallback.style.display = 'block';
    }
  }
}

/**
 * メイン処理
 */
async function main(): Promise<void> {
  showStatus('読み込み中...');

  try {
    // LIFF SDKの初期化
    await liff.init({ liffId: LIFF_ID });

    // 未ログインの場合はLINEログインにリダイレクト
    if (!liff.isLoggedIn()) {
      liff.login({ redirectUri: window.location.href });
      return;
    }

    showStatus('情報を取得中...');

    // LINE User IDを取得
    const profile = await liff.getProfile();
    const lineUserId = profile.userId;
    // ID トークン（openid スコープ未設定の LIFF アプリでは null）。
    // バックエンドの line_user_id 検証 (#263) に使う。
    const idToken = liff.getIDToken();

    const webTo = new URLSearchParams(window.location.search).get('lct_to');
    if (webTo) {
      await goToWebDestination(lineUserId, idToken, webTo);
      return;
    }

    // URLパラメータ（クリックID・UTM）を取得
    const params = extractParams();

    if (Object.keys(params).length > 0) {
      showStatus('計測データを送信中...');
      // バックエンドにトラッキングデータを送信
      await sendTrackingData(lineUserId, idToken, params);
    }

    // 友だち追加状態を確認（リンクされたボット未設定時はスキップ）
    let isFriend = false;
    try {
      const friendship = await liff.getFriendship();
      isFriend = friendship.friendFlag;
    } catch {
      console.warn('[LIFF] getFriendship失敗（ボットリンク未設定の可能性）');
    }

    if (!isFriend) {
      showStatus('友だち追加ページに移動します...');
      redirectToFriendAdd();
    } else {
      showStatus('すでに友だち登録済みです。');
    }
  } catch (error) {
    console.error('[LIFF] エラー:', error);
    const msg = error instanceof Error ? error.message : String(error);
    showStatus(`エラー: ${msg}`, true);

    // エラーが発生しても友だち追加ページへのリンクを表示
    const fallback = document.getElementById('fallback');
    if (fallback) {
      fallback.style.display = 'block';
    }
  }
}

// DOMロード後に実行
document.addEventListener('DOMContentLoaded', main);
